import { ApiTwoTone, ControlOutlined } from '@ant-design/icons';
import { Button } from 'antd';
import React, { useCallback, useEffect } from 'react';
import { useTranslation } from 'react-i18next';

import { EAbilityActions, EAbilitySubjects } from '@client/hooks/useAbilities';
import { useAuth } from '@client/hooks/useAuth';
import { useFeatureQueries } from '@client/queries/useFeatureQueries';
import { useDrawerActions } from '@client/store/drawer.store';
import { useFeature } from '@client/store/feature.store';
import { useLoading } from '@client/store/loading.store';

import styles from '../../subscriptions.module.less';

import { FeaturesDrawerContent } from './FeaturesDrawerContent';

type TFeaturesDrawerButtonProps = {
  disabled?: boolean;
};

export const FeaturesDrawerButton: React.FC<TFeaturesDrawerButtonProps> = ({
  disabled,
}) => {
  const { setOpenDrawer, setConfigDrawer, setOnRefreshDrawer } =
    useDrawerActions();
  const { ability } = useAuth();
  const { featureType } = useFeature();
  const { t } = useTranslation();
  const { useActiveFeatures } = useFeatureQueries();

  const { refetch, isLoading, isFetching } = useActiveFeatures(featureType);

  const { loading } = useLoading(isLoading || isFetching);

  const handleRefresh = useCallback(async () => {
    await refetch();
  }, [refetch]);

  useEffect(() => {
    // Set up the refresh function for the drawer
    setOnRefreshDrawer(handleRefresh);
  }, [handleRefresh, setOnRefreshDrawer]);

  const handleShowFeatures = (e: React.MouseEvent) => {
    e.preventDefault();
    e.stopPropagation();

    setOpenDrawer(true);
    setConfigDrawer({
      size: 600,
      children: <FeaturesDrawerContent />,
      title: (
        <div className={styles.chipLabel}>
          <ControlOutlined />
          {t('features')}
        </div>
      ),
    });
  };

  return (
    <Button
      loading={loading}
      disabled={
        disabled ||
        ability.cannot(
          EAbilityActions.ASSIGN,
          `${EAbilitySubjects.SUBSCRIPTION}.${EAbilitySubjects.FEATURES}`,
        )
      }
      icon={<ApiTwoTone />}
      onClick={handleShowFeatures}
    >
      {t('actions.show', { type: t('features') })}
    </Button>
  );
};
